import { useRef } from "react";
import { Bell, Download } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { CoinMark, Logo } from "@/components/brand/Logo";
import { PageHeader } from "@/components/common/PageHeader";

const PALETTE = [
  ["Bleu nuit", "#142F39"], ["Vert menthe", "#B9EBD7"], ["Vert sauge", "#87B8A5"],
  ["Blanc cassé", "#F8FAF8"], ["Gris clair", "#EDF2F1"], ["Doré doux", "#D8BD78"],
];

const save = (url, name) => { const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); };

let fontCss;
const toData = (blob) => new Promise((ok) => { const r = new FileReader(); r.onload = () => ok(r.result); r.readAsDataURL(blob); });
const embedFonts = async () => {
  if (fontCss) return fontCss;
  const href = [...document.querySelectorAll("link[href*='fonts.googleapis.com/css']")].map((l) => l.href).find((h) => h.includes("Outfit"));
  if (!href) return (fontCss = "");
  let css = await (await fetch(href)).text();
  const blocks = css.split("@font-face").filter((b) => b.includes("Outfit") && b.includes("U+0000-00FF"));
  css = "";
  for (const b of blocks) {
    const url = b.match(/url\((https:[^)]+)\)/)?.[1];
    if (url) css += `@font-face${b.replace(url, await toData(await (await fetch(url)).blob()))}`;
  }
  return (fontCss = css);
};

const Dl = ({ onClick, href, name, label, testid }) => (
  <a href={href || "#"} download={href ? name : undefined} onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold transition-colors hover:bg-muted"><Download className="h-3 w-3" />{label}</a>
);

const Tile = ({ label, className = "", children, testid, slug, files }) => {
  const ref = useRef(null);
  const exp = (kind) => async (e) => {
    e.preventDefault();
    try {
      const node = ref.current.firstElementChild;
      const url = await toPng(node, { cacheBust: true, fontEmbedCSS: await embedFonts(), pixelRatio: 6 });
      save(url, `cashmy-${slug}.${kind}`);
    } catch { toast.error("Export impossible, réessayez"); }
  };
  return (
    <figure className="fade-up flex flex-col gap-3" data-testid={testid}>
      <div ref={ref} className={`flex min-h-44 items-center justify-center rounded-2xl border border-border p-8 ${className}`}>{children}</div>
      <figcaption className="flex flex-wrap items-center gap-2 text-xs font-medium text-muted-foreground"><span className="mr-auto">{label}</span>
        {files ? <Dl href={files[0][0]} name={files[0][0].split("/").pop()} label="Télécharger" testid={`${testid}-download`} />
          : <Dl onClick={exp("png")} label="Télécharger" testid={`${testid}-download`} />}
      </figcaption>
    </figure>
  );
};

export default function Brand() {
  return (
    <div className="space-y-10" data-testid="brand-page">
      <PageHeader eyebrow="Identité visuelle" title="La pièce CASHMY" text="Une pièce, un C : la maîtrise de son argent et la valeur de ses biens. Sources vectorielles dans /public/brand, exports PNG dans /public/icons." />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Tile label="Logo horizontal — clair" className="bg-[#F8FAF8]" testid="brand-horizontal-light" slug="logo-horizontal-clair"><Logo size={48} textClassName="text-2xl text-[#142F39]" /></Tile>
        <Tile label="Logo horizontal — sombre" className="bg-[#142F39]" testid="brand-horizontal-dark" slug="logo-horizontal-sombre"><Logo size={48} textClassName="text-2xl text-[#F8FAF8]" /></Tile>
        <Tile label="Logo vertical" className="mint-glow" testid="brand-vertical" slug="logo-vertical"><Logo layout="vertical" size={72} /></Tile>
        <Tile label="Icône pièce" className="bg-card" testid="brand-icon" slug="piece" files={[["/icons/icon-512.png", "PNG"]]}><CoinMark size={120} /></Tile>
        <Tile label="Pièce variante nuit" className="bg-[#EDF2F1]" testid="brand-icon-night" slug="piece-nuit"><CoinMark size={120} variant="night" /></Tile>
        <Tile label="Monochrome" className="bg-card text-[#142F39] dark:text-[#F8FAF8]" testid="brand-mono" slug="piece-monochrome"><CoinMark size={110} variant="mono" /></Tile>
        <Tile label="Icône iOS / Android (marge de sécurité 22 %)" className="bg-secondary" testid="brand-app-icon" files={[["/icons/ios-1024.png", "iOS 1024"], ["/icons/android-adaptive-432.png", "Android"], ["/icons/maskable-512.png", "Maskable"], ["/brand/icon-app.svg", "SVG"]]}>
          <img src="/icons/ios-1024.png" alt="Icône application" className="h-28 w-28 rounded-[1.6rem] shadow-lg" />
        </Tile>
        <Tile label="Favicon & notification" className="bg-card" testid="brand-favicon" files={[["/icons/favicon-32.png", "Favicon"], ["/icons/apple-touch-icon-180.png", "Apple 180"], ["/icons/notification-96.png", "Notification"]]}>
          <div className="flex items-center gap-6">
            <img src="/icons/favicon-32.png" alt="Favicon" className="h-8 w-8" />
            <div className="flex items-center gap-3 rounded-2xl bg-[#142F39] px-4 py-3 text-xs text-[#F8FAF8]">
              <img src="/icons/notification-96.png" alt="" className="h-5 w-5" /><span>Garantie bientôt expirée</span><Bell className="h-3.5 w-3.5 text-mint" />
            </div>
          </div>
        </Tile>
        <Tile label="Écran de démarrage" className="bg-secondary" testid="brand-splash" files={[["/icons/splash-1284x2778.png", "PNG"]]}>
          <img src="/icons/splash-1284x2778.png" alt="Écran de démarrage" className="h-44 rounded-xl shadow-lg" />
        </Tile>
      </div>
      <section>
        <h2 className="text-lg font-bold">Palette</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {PALETTE.map(([n, hex]) => (
            <div key={hex} className="card-surface overflow-hidden" data-testid={`palette-${hex.slice(1)}`}>
              <div className="h-16" style={{ background: hex }} />
              <div className="p-3"><div className="text-sm font-medium">{n}</div><div className="num text-xs text-muted-foreground">{hex}</div></div>
            </div>
          ))}
        </div>
      </section>
      <section className="card-surface p-6">
        <h2 className="text-lg font-bold">Typographie</h2>
        <div className="mt-4 space-y-2">
          <div className="font-heading text-3xl font-extrabold">Outfit — titres</div>
          <div className="text-base">DM Sans — textes courants, lisible sur petit écran.</div>
          <div className="num text-xl font-bold">JetBrains Mono — 1 240,50 €</div>
        </div>
      </section>
    </div>
  );
}
